import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, Mail, Phone, Gift, Calendar, Package, 
  LogOut, Clock, CheckCircle, Truck, Loader2,
  ArrowLeft, CreditCard, RefreshCw, Star, MessageSquare
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import ReviewForm from './ReviewForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerAccount = () => {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [rescheduleBooking, setRescheduleBooking] = useState(null);
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(null); // booking id to review

  const availableTimes = [
    "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
    "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM"
  ];

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  useEffect(() => {
    // Check if customer is logged in
    const storedCustomer = sessionStorage.getItem('customer');
    if (!storedCustomer) {
      navigate('/login');
      return;
    }
    
    const customerData = JSON.parse(storedCustomer);
    setCustomer(customerData);
    fetchCustomerData(customerData.id);
  }, [navigate]);

  const fetchCustomerData = async (customerId) => {
    try {
      // Fetch updated profile
      const profileResponse = await axios.get(`${API}/customers/profile/${customerId}`);
      if (profileResponse.data.success) {
        setCustomer(profileResponse.data.customer);
        sessionStorage.setItem('customer', JSON.stringify(profileResponse.data.customer));
      }

      // Fetch customer bookings
      const bookingsResponse = await axios.get(`${API}/customers/${customerId}/bookings`);
      if (bookingsResponse.data.success) {
        setBookings(bookingsResponse.data.bookings);
      }
    } catch (error) {
      console.error('Error fetching customer data:', error);
      toast.error('Failed to load account data');
    } finally {
      setLoading(false);
    }
  };

  const handleReschedule = async (bookingId) => {
    if (!newDate) {
      toast.error('Please select a new date');
      return;
    }

    setIsRescheduling(true);
    try {
      await axios.post(`${API}/bookings/${bookingId}/reschedule`, {
        pickupDate: newDate,
        pickupTime: newTime || rescheduleBooking.pickupTime
      });

      toast.success('Booking rescheduled successfully!');
      setRescheduleBooking(null);
      setNewDate('');
      setNewTime('');
      
      // Refresh bookings
      if (customer?.id) {
        fetchCustomerData(customer.id);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to reschedule';
      toast.error(message);
    } finally {
      setIsRescheduling(false);
    }
  };

  const canReschedule = (booking) => {
    return booking.status === 'pending' && 
           (booking.reschedule_count || 0) < 1;
  };

  const canReview = (booking) => {
    return (booking.status === 'completed' || booking.status === 'paid') && 
           !booking.review_submitted;
  };

  const handleLogout = () => {
    sessionStorage.removeItem('customer');
    sessionStorage.removeItem('customerToken');
    toast.success('Logged out successfully');
    navigate('/');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="text-yellow-500" size={18} />;
      case 'picked-up':
        return <Truck className="text-blue-500" size={18} />;
      case 'in-progress':
        return <Loader2 className="text-purple-500 animate-spin" size={18} />;
      case 'completed':
        return <CheckCircle className="text-green-500" size={18} />;
      case 'paid':
        return <CreditCard className="text-teal-500" size={18} />;
      default:
        return <Clock className="text-gray-500" size={18} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'picked-up':
        return 'bg-blue-100 text-blue-800';
      case 'in-progress':
        return 'bg-purple-100 text-purple-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'paid':
        return 'bg-teal-100 text-teal-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-teal-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-teal-600 animate-spin" />
      </div>
    );
  }

  const loyaltyProgress = customer?.loyalty_count || 0;
  const progressPercentage = (loyaltyProgress / 10) * 100;
  const washesUntilFree = 10 - loyaltyProgress;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-teal-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-6">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20"
              onClick={() => navigate('/')}
            >
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">My Account</h1>
              <p className="text-teal-100">Welcome back, {customer?.name}!</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="text-white hover:bg-white/20"
            onClick={handleLogout}
          >
            <LogOut size={18} className="mr-2" />
            Logout
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Loyalty Progress Card */}
        <Card className="mb-8 border-2 border-teal-200 bg-gradient-to-r from-teal-50 to-cyan-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 rounded-full bg-teal-600 flex items-center justify-center">
                <Gift className="text-white" size={32} />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-gray-900">Loyalty Rewards</h2>
                <p className="text-gray-600">
                  {washesUntilFree > 0 
                    ? `${washesUntilFree} more wash${washesUntilFree > 1 ? 'es' : ''} until your FREE service!`
                    : '🎉 Your next service is FREE!'
                  }
                </p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-bold text-teal-600">{loyaltyProgress}/10</div>
                <p className="text-sm text-gray-500">Completed</p>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-teal-500 to-cyan-500 transition-all duration-500 rounded-full"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            <div className="flex justify-between mt-2 text-xs text-gray-500">
              <span>Start</span>
              <span>FREE Service!</span>
            </div>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Profile Info */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="text-teal-600" size={20} />
                Profile
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <User className="text-gray-500" size={18} />
                <div>
                  <p className="text-xs text-gray-500">Name</p>
                  <p className="font-medium">{customer?.name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Mail className="text-gray-500" size={18} />
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="font-medium">{customer?.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Phone className="text-gray-500" size={18} />
                <div>
                  <p className="text-xs text-gray-500">Phone</p>
                  <p className="font-medium">{customer?.phone}</p>
                </div>
              </div>
              
              <div className="pt-4 border-t">
                <Button
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white"
                  onClick={() => navigate('/#book-now')}
                >
                  <Package className="mr-2" size={18} />
                  Book New Pickup
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Booking History */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="text-teal-600" size={20} />
                Booking History
              </CardTitle>
              <CardDescription>
                {bookings.length} total booking{bookings.length !== 1 ? 's' : ''}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {bookings.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="mx-auto text-gray-300 mb-4" size={48} />
                  <p className="text-gray-500">No bookings yet</p>
                  <Button
                    className="mt-4 bg-teal-600 hover:bg-teal-700"
                    onClick={() => navigate('/#book-now')}
                  >
                    Book Your First Pickup
                  </Button>
                </div>
              ) : (
                <div className="space-y-4 max-h-[500px] overflow-y-auto">
                  {bookings.map((booking) => (
                    <div 
                      key={booking.id} 
                      className="p-4 border rounded-lg hover:border-teal-300 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-semibold text-gray-900">
                            Pickup: {new Date(booking.pickupDate).toLocaleDateString()}
                          </p>
                          <p className="text-sm text-gray-500">{booking.city}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(booking.status)}`}>
                          {getStatusIcon(booking.status)}
                          {booking.status}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {booking.pickupTime && (
                          <div className="text-gray-600">
                            <span className="text-gray-400">Time:</span> {booking.pickupTime}
                          </div>
                        )}
                        {booking.amount > 0 && (
                          <div className="text-gray-600">
                            <span className="text-gray-400">Amount:</span> ${booking.amount.toFixed(2)}
                          </div>
                        )}
                        {booking.loyalty_applied && (
                          <div className="col-span-2 text-green-600 font-medium">
                            🎁 FREE Service Applied!
                          </div>
                        )}
                        {booking.reschedule_count > 0 && (
                          <div className="col-span-2 text-amber-600 text-xs">
                            📅 Rescheduled from {booking.original_pickup_date}
                          </div>
                        )}
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t">
                        {/* Reschedule Button */}
                        {canReschedule(booking) && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-amber-600 border-amber-300 hover:bg-amber-50"
                            onClick={() => {
                              setRescheduleBooking(booking);
                              setNewDate('');
                              setNewTime('');
                            }}
                          >
                            <RefreshCw size={14} className="mr-1" />
                            Reschedule
                          </Button>
                        )}
                        
                        {/* Review Button */}
                        {canReview(booking) && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-teal-600 border-teal-300 hover:bg-teal-50"
                            onClick={() => setShowReviewForm(booking.id)}
                          >
                            <Star size={14} className="mr-1" />
                            Leave Review
                          </Button>
                        )}
                        
                        {/* Already Reviewed Badge */}
                        {booking.review_submitted && (
                          <span className="text-xs text-green-600 flex items-center gap-1">
                            <CheckCircle size={12} />
                            Review Submitted
                          </span>
                        )}
                      </div>
                      
                      <p className="text-xs text-gray-400 mt-2">
                        Booked on {new Date(booking.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-teal-600">{customer?.total_bookings || 0}</p>
              <p className="text-sm text-gray-500">Total Bookings</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-600">{customer?.completed_bookings || 0}</p>
              <p className="text-sm text-gray-500">Completed</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-purple-600">{loyaltyProgress}</p>
              <p className="text-sm text-gray-500">Loyalty Points</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-600">${customer?.total_spent?.toFixed(2) || '0.00'}</p>
              <p className="text-sm text-gray-500">Total Spent</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Reschedule Modal */}
      {rescheduleBooking && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <RefreshCw className="text-amber-600" size={20} />
                Reschedule Pickup
              </CardTitle>
              <CardDescription>
                Current: {rescheduleBooking.pickupDate} at {rescheduleBooking.pickupTime}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Pickup Date *
                </label>
                <input
                  type="date"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                  min={getMinDate()}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Pickup Time (Optional)
                </label>
                <select
                  value={newTime}
                  onChange={(e) => setNewTime(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  <option value="">Keep current time ({rescheduleBooking.pickupTime || 'TBD'})</option>
                  {availableTimes.map((time) => (
                    <option key={time} value={time}>{time}</option>
                  ))}
                </select>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                ⚠️ <strong>Note:</strong> You can only reschedule once and must do so at least 2 hours before pickup.
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setRescheduleBooking(null)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleReschedule(rescheduleBooking.id)}
                  disabled={isRescheduling || !newDate}
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                >
                  {isRescheduling ? 'Rescheduling...' : 'Confirm Reschedule'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Review Modal */}
      {showReviewForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-lg">
            <ReviewForm
              bookingId={showReviewForm}
              customerName={customer?.name}
              onClose={() => setShowReviewForm(null)}
              onSubmitted={() => {
                setShowReviewForm(null);
                if (customer?.id) {
                  fetchCustomerData(customer.id);
                }
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerAccount;
